import FullCalendar from '@fullcalendar/react';
import { WebPageEvents } from '@griply/types';
import { getTimeFormat } from '@griply/utils';
import { startOfDay, startOfWeek } from 'date-fns';
import { useCalendarTaskSync } from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useSortedSmartLists } from 'features/smart-lists';
import { useSortedTags } from 'features/tags';
import { useTaskTodayControls } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarSlotContextMenu } from 'shared/components/connected/calendar-slot-context-menu';
import { FullCalendarWeek } from 'shared/components/ui/full-calendar-week';
import { weekdaysNumberMap } from 'shared/constants';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useCalendarEventControls } from 'shared/hooks/use-calendar-event-controls';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { useUserTimeZone } from 'shared/hooks/use-user-time-zone';
import { Paths } from 'shared/routes/paths';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { Timestamp } from 'shared/types/timestamp';
import { getGoalDetailUrl } from 'shared/utils/get-goal-detail-url';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { getScheduleByDate } from 'shared/utils/schedules';

import { useUpcomingCalendarEvents } from '../../hooks/use-upcoming-calendar-events';

const dayRange = 7;

export type WeekProps = {
  rangeReferenceDate?: Timestamp;
  scrollTime?: string;
  fullCalendarRef?: React.RefObject<FullCalendar | null>;
};

export const Week: React.FC<WeekProps> = ({
  rangeReferenceDate,
  scrollTime,
  fullCalendarRef,
}) => {
  useTrackPageVisitEvent(WebPageEvents.UpcomingWeek, undefined);
  const timeZone = useUserTimeZone();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const links = useEntityLinks();
  const tags = useSortedTags();
  const smartLists = useSortedSmartLists();

  const startDate = useMemo(
    () =>
      startOfDay(
        rangeReferenceDate ??
          startOfWeek(today, {
            weekStartsOn: weekdaysNumberMap[weekStartsOn],
          }),
      ),
    [rangeReferenceDate, today, weekStartsOn],
  );
  const { tasks: filteredTasks, events } = useUpcomingCalendarEvents({
    startDate,
    dayRange,
  });

  const navigate = useNavigate();
  const openTaskDetail = useOpenTaskDetail();
  const { onComplete } = useTaskTodayControls();
  const {
    onEventChange,
    onEventReceive,
    onSelect,
    onDateTime,
    slotContextMenu,
  } = useCalendarEventControls(
    lifeAreas,
    goals,
    filteredTasks,
    tags,
    smartLists,
  );
  const calendarSync = useCalendarTaskSync(filteredTasks);

  const _openEventDetail = useCallback(
    (id: ID) => {
      const task = filteredTasks.find(({ id: taskId }) => taskId === id);
      if (task) {
        openTaskDetail(task);
        return;
      }

      const goal = goals.find(({ id: goalId }) => goalId === id);
      if (goal) {
        navigate(getGoalDetailUrl(goal));
        return;
      }

      const lifeArea = lifeAreas.find(({ id: goalId }) => goalId === id);
      if (lifeArea) {
        navigate(replaceUrlParams(Paths.LifeAreaDetail, { id: lifeArea.id }));
        return;
      }
    },
    [filteredTasks, goals, lifeAreas, openTaskDetail, navigate],
  );

  const _onComplete = useCallback(
    (id: ID, date: Timestamp) => {
      const task = filteredTasks.find(({ id: taskId }) => taskId === id);
      if (!task) {
        return;
      }

      const daySchedule = getScheduleByDate(task.schedules, { date, today });

      onComplete(task, ScheduleEntryState.Complete, {
        referenceDate: date,
        toggleDayEntry: !!daySchedule?.frequency,
      });
    },
    [filteredTasks, today, onComplete],
  );

  return (
    <>
      <FullCalendarWeek
        fullCalendarRef={fullCalendarRef}
        tasks={filteredTasks}
        goals={goals}
        lifeAreas={lifeAreas}
        links={links}
        tags={tags}
        smartLists={smartLists}
        onCompleteTask={_onComplete}
        onClickEvent={_openEventDetail}
        calendarSync={calendarSync}
        events={events}
        rangeReferenceDate={startDate}
        scrollTime={scrollTime}
        rangeDays={dayRange}
        eventChange={onEventChange}
        eventReceive={onEventReceive}
        dateClick={onDateTime}
        select={onSelect}
        selectConstraint={{
          startTime: '00:00',
          endTime: '23:59:59',
        }}
        timeFormat={getTimeFormat(user)}
        weekStartsOn={weekStartsOn}
        timeZone={timeZone}
        selectable
        editable
        droppable
      />

      {slotContextMenu.slot && <CalendarSlotContextMenu {...slotContextMenu} />}
    </>
  );
};
